import React from 'react';
import { CheckCircle, Target, Award, Users, Sparkles } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: CheckCircle,
      title: 'Honest Communication',
      description: 'No hidden costs, confusing jargon, or unreliable timelines'
    },
    {
      icon: Award,
      title: 'Quality Workmanship',
      description: 'Brand-level procedures guided by data, not guesswork'
    },
    {
      icon: Target,
      title: 'Convenience First',
      description: 'Processes designed for simplicity, not complication'
    },
    {
      icon: Users,
      title: 'Consistent Standards',
      description: 'Service standards that stay reliable, every time'
    }
  ];

  const milestones = [
    { number: '50K+', label: 'Services Completed' },
    { number: '16+', label: 'Service Centers' },
    { number: '4.8/5', label: 'Average Rating' }
  ];

  return (
    <div data-testid="about-page" className="min-h-screen bg-slate-950 text-slate-200">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950 pt-24 pb-20">
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(6,182,212,0.15),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_70%,rgba(59,130,246,0.12),transparent_55%)]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6">
            <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
            <span className="text-xs uppercase tracking-[0.2em] text-cyan-300 font-medium">About Us</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white max-w-3xl">
            Built for drivers who <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">demand better</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-slate-300 leading-relaxed">
            Not a garage aggregator. Your independent advisor — reviewing every concern, every quote, every time.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7 space-y-5">
              <span className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-medium">The Story</span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight">
                A better way to service your car
              </h2>
              <div className="space-y-4 text-slate-300 leading-relaxed">
                <p>
                  Propitstop was created with a simple belief: car service should be transparent, reliable, and genuinely professional.
                  Founded by <strong className="text-white">Sathish D</strong>, Propitstop brings together over 15 years of expertise with
                  leading luxury automotive brands like Mercedes-Benz, BMW, and Volkswagen, along with operational leadership from one of
                  India's largest multi-brand service networks.
                </p>
                <p>
                  During his years in workshops and leading nationwide service teams, Sathish noticed a clear problem — most vehicle owners
                  struggle to find service they can trust. Quality varied garage to garage, communication was inconsistent, and customers
                  rarely felt in control.
                </p>
                <p className="text-white font-semibold">Propitstop was built to solve that.</p>
                <p>
                  We focus on professional diagnostics, transparent processes, and consistent standards. Every technician follows brand-level
                  procedures. Every repair is guided by data. Every customer is kept informed at every step.
                </p>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden border border-slate-800 shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1551522435-a13afa10f103?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxnYXJhZ2V8ZW58MHx8fHwxNzYxODMzMDAzfDA&ixlib=rb-4.1.0&q=85"
                  alt="Our Workshop"
                  className="w-full h-[420px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 p-4 rounded-xl bg-slate-900/70 backdrop-blur-md border border-white/5">
                  <p className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-medium mb-1">Workshop</p>
                  <p className="text-white font-semibold">Modern equipment. Certified technicians. Zero guesswork.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 to-slate-900 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <span className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-medium">Our principles</span>
            <h2 className="mt-2 text-3xl md:text-4xl font-extrabold text-white tracking-tight">What we stand for</h2>
            <p className="mt-3 text-slate-400">The rules we won't compromise on.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  data-testid={`about-value-${index}`}
                  className="group relative bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 hover:border-cyan-500/40 hover:-translate-y-1 transition-[transform,border-color,box-shadow] duration-300 hover:shadow-[0_0_25px_rgba(6,182,212,0.15)]"
                >
                  <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-cyan-400" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{value.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 border border-slate-800">
            <div className="absolute inset-0 opacity-40 pointer-events-none">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(6,182,212,0.25),transparent_55%)]" />
            </div>
            <div className="relative px-8 py-16 md:px-16 md:py-20">
              <div className="max-w-xl mb-10">
                <span className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-medium">By the numbers</span>
                <h2 className="mt-2 text-3xl md:text-4xl font-extrabold text-white tracking-tight">The trust we've earned</h2>
              </div>
              <div className="grid md:grid-cols-3 gap-10">
                {milestones.map((m, i) => (
                  <div key={i} data-testid={`milestone-${i}`}>
                    <div className="text-5xl lg:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400 tracking-tight">
                      {m.number}
                    </div>
                    <div className="mt-2 text-sm uppercase tracking-[0.2em] text-slate-400">{m.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Promise */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <span className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-medium">Our Promise</span>
          <p className="mt-6 text-2xl md:text-3xl font-extrabold text-white tracking-tight leading-tight">
            At Propitstop, we don't just service cars.
          </p>
          <p className="mt-3 text-2xl md:text-3xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400 leading-tight">
            We build confidence, trust, and long-term relationships.
          </p>
        </div>
      </section>
    </div>
  );
};

export default About;
